import "temporal-polyfill/global";

/**
 * Some browsers (e.g. Chrome on Windows after a time zone database update)
 * report `undefined` for `Intl.DateTimeFormat().resolvedOptions().timeZone`.
 * temporal-polyfill reads its default zone from there, so every
 * `Temporal.Now.*` call throws. Fall back to a fixed UTC offset ID, which both
 * Temporal and Intl accept.
 */
export function fixedOffsetTimeZoneId(timezoneOffsetMinutes: number): string {
  const offsetMinutes = -timezoneOffsetMinutes;
  const sign = offsetMinutes < 0 ? "-" : "+";
  const absolute = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absolute / 60)).padStart(2, "0");
  const minutes = String(absolute % 60).padStart(2, "0");
  return `${sign}${hours}:${minutes}`;
}

const wrappers = new WeakSet<object>();

export function installIntlTimeZoneFallback(): void {
  const proto = Intl.DateTimeFormat.prototype;
  const original = proto.resolvedOptions;
  if (wrappers.has(original)) {
    return;
  }
  const wrapper = function resolvedOptions(this: Intl.DateTimeFormat) {
    const options = original.call(this);
    if (options.timeZone === undefined) {
      return {
        ...options,
        timeZone: fixedOffsetTimeZoneId(new Date().getTimezoneOffset()),
      };
    }
    return options;
  };
  wrappers.add(wrapper);
  proto.resolvedOptions = wrapper;
}

/**
 * temporal-polyfill captures the native `Intl.DateTimeFormat` when it loads, so
 * wrapping `Intl` afterwards (or in an unspecified bundle order) does not reach
 * its default time zone. Wrap the `Temporal.Now` functions that use it.
 */
export function installTemporalNowTimeZoneFallback(): void {
  if (typeof Temporal.Now.timeZoneId() === "string") {
    return;
  }
  const fallback = () => fixedOffsetTimeZoneId(new Date().getTimezoneOffset());
  const now = Temporal.Now;
  const { plainDateISO, plainDateTimeISO, plainTimeISO, zonedDateTimeISO } =
    now;
  now.timeZoneId = fallback;
  now.plainDateISO = (timeZone = fallback()) =>
    plainDateISO.call(now, timeZone);
  now.plainDateTimeISO = (timeZone = fallback()) =>
    plainDateTimeISO.call(now, timeZone);
  now.plainTimeISO = (timeZone = fallback()) =>
    plainTimeISO.call(now, timeZone);
  now.zonedDateTimeISO = (timeZone = fallback()) =>
    zonedDateTimeISO.call(now, timeZone);
}

/**
 * Loads temporal-polyfill and works around browsers that report no time zone.
 * Import and call this instead of importing `temporal-polyfill/global`.
 */
export function installTemporalPolyfill(): void {
  installIntlTimeZoneFallback();
  installTemporalNowTimeZoneFallback();
}

installTemporalPolyfill();
